﻿import { XHRFetcher } from "../xhr_fetcher";
import { executeScriptsInNode } from "../../helpers/scripts";

/**
 * Nodalizer: A versatile utility for fetching, parsing, and manipulating HTML content from remote endpoints.
 *
 * Purpose:
 *   The Nodalizer class simplifies the process of retrieving HTML snippets from a server,
 *   extracting specific elements from that HTML, and optionally inserting those elements into the
 *   current document's DOM. It offers flexibility in how the fetched content is handled,
 *   allowing for pre-insertion manipulation or simply extracting elements for use elsewhere.
 *
 * Core Functionality:
 *   1. Fetching: Retrieves HTML content from a specified URL using an XHRFetcher.
 *   2. Parsing: Parses the fetched HTML into a DOM Document object.
 *   3. Extraction: Extracts specific elements from the parsed HTML based on user-defined CSS selectors.
 *   4. Insertion: Inserts the extracted elements into the current document's DOM, replacing target elements
 *      specified by CSS selectors or DOM elements.
 *   5. Preparation: Allows for the preparation of content (fetching and extracting) without immediate DOM
 *      manipulation, enabling pre-insertion processing.
 *
 * Key Features:
 *   - Mappings: Uses a `mappings` object to define the relationship between source elements (in the fetched HTML)
 *     and target elements (in the current document). The keys of the `mappings` object are CSS selectors for the
 *     source elements, and the values are CSS selectors or DOM elements for the target elements.
 *   - Prepare and Insert: Supports a two-step process of `prepare()` (fetch and extract) and `insert()` (insert
 *     into the DOM), providing fine-grained control over the insertion process.
 *   - Flexible Usage: Can be used to:
 *     - Fetch and immediately insert content into the DOM (`insertIntoDOM()`).
 *     - Fetch and extract content for later use, without immediate insertion (`prepare()`).
 *     - Fetch, extract, and then insert content with pre-insertion manipulation (`prepare()` followed by `insert()`).
 *     - Simply fetch and extract content for use in other code, without ever inserting into the DOM.
 *   - Error Handling: Includes robust error handling for network issues, missing elements, and invalid target types.
 *   - Script Execution: Executes any inline `<script>` tags found within the fetched content.
 *   - Custom Document: Supports the use of a custom `Document` object for testing or working in non-browser environments.
 *
 * Usage Patterns:
 *
 *   1. Immediate Insertion:
 *
 * ```javascript
 * const nodalizer = new Nodalizer('/some/url', { '.source': '.target' });
 * nodalizer.insertIntoDOM().then(elements => {
 *  // Handle the inserted elements
 * });
 * ```
 *
 * 2. Prepare and Insert:
 *
 * ```javascript
 * const nodalizer = new Nodalizer('/some/url', { '.source1': '.target1', '.source2': '.target2' });
 * nodalizer.prepare().then(sourceElements => {
 *  // Manipulate sourceElements if needed
 *  return nodalizer.insert(sourceElements);
 * }).then(insertedElements => {
 *  // Handle the inserted elements
 * }).catch(error => {
 *  // Handle errors
 * });
 * ```
 *
 * 3. Prepare Only (No Insertion):
 *
 * ```javascript
 * const nodalizer = new Nodalizer('/some/url', { '.source1': '.target1', '.source2': '.target2' });
 * nodalizer.prepare().then(sourceElements => {
 *  // Use sourceElements in other code, without inserting them
 *  console.log(sourceElements);
 * }).catch(error => {
 *  // Handle errors
 * });
 * ```
 *
 * Dependencies:
 *   - XHRFetcher: For fetching HTML content from a URL.
 *   - executeScriptsInNode: For executing inline script tags.
 *
 * @class Nodalizer
 */

const LOG_TAG = "Nodalizer";

export default class Nodalizer {

    /**
     * Constructs a Nodalizer instance.
     * @param {string} url - The URL of the HTML content to fetch.
     * @param {Object} mappings - An object mapping CSS selectors (or DOM elements) in the fetched HTML to targets in the current DOM.
     * @param {Document} [customDocument=document] - The document object to be used for DOM manipulation. Defaults to the global document object.
     */
    constructor(url, mappings, customDocument = document) {
        this.url = url;
        this.mappings = mappings;
        this.fetcher = new XHRFetcher(url);
        this.document = customDocument;
        this._preparedData = null; // Store prepared data here
    }

    /**
     * Prepares the HTML content for insertion without modifying the DOM.
     * This provides an opportunity to manipulate or otherwise use the source nodes before insertion.
     * @returns {Promise<Element[]>} A promise that resolves to an array of the source elements.
     */
    async prepare() {
        try {
            const html = await this.fetcher.fetch();
            if (!html) {
                const message = "No HTML content fetched";
                this.log("error", message, html);
                return Promise.reject(message);
            }

            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const sourceElements = this._extractSourceElements(doc);

            this._preparedData = sourceElements; // Store the source elements
            return sourceElements;
        } catch (error) {
            this.log("error", "Error in Nodalizer.prepare", error);
            return Promise.reject(error);
        }
    }

    /**
     * Inserts content into the DOM based on the mappings and returns a promise with the updated elements.
     * @returns {Promise<Element[]>} A promise that resolves to an array of updated elements.
     */
    async insertIntoDOM() {
        try {
            const elementsToInsert = await this._getElementsToInsert();
            return this._insertElements(elementsToInsert);
        } catch (error) {
            this.log("error", "Error in Nodalizer.insertIntoDOM", error);
            return Promise.reject(error);
        }
    }

    /**
     * Inserts prepared content into the DOM based on the mappings and returns a promise with the updated elements.
     * @param {Element[]} preparedElements - A pre-parsed array of source elements to use for insertion.
     * @returns {Promise<Element[]>} A promise that resolves to an array of updated elements.
     */
    async insert(preparedElements) {
        try {
            return this._insertElements(preparedElements);
        } catch (error) {
            this.log("error", "Error in Nodalizer.insert", error);
            return Promise.reject(error);
        }
    }

    /**
     * Extracts the source elements from the parsed HTML document based on the mappings.
     * @param {Document} doc - The parsed HTML document.
     * @returns {Element[]} An array of the source elements.
     * @private
     */
    _extractSourceElements(doc) {
        const sourceElements = [];
        for (const sourceSelector of Object.keys(this.mappings)) {
            const sourceElement = doc.querySelector(sourceSelector);
            if (sourceElement) {
                sourceElements.push(sourceElement);
            } else {
                this.log(
                    "warn",
                    `No element found for selector: ${sourceSelector}`,
                    sourceSelector,
                );
            }
        }
        return sourceElements;
    }

    /**
     * Gets the elements to insert, either from prepared data or by fetching and parsing.
     * @returns {Promise<Element[]>} A promise that resolves to an array of elements to insert.
     * @private
     */
    async _getElementsToInsert() {
        if (this._preparedData) {
            return this._preparedData;
        } else {
            const html = await this.fetcher.fetch();
            if (!html) {
                const message = "No HTML content fetched";
                this.log("error", message, html);
                return Promise.reject(message);
            }
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            return this._extractSourceElements(doc);
        }
    }

    /**
     * Inserts the given elements into the DOM based on the mappings.
     * @param {Element[]} elementsToInsert - The elements to insert.
     * @returns {Promise<Element[]>} A promise that resolves to an array of updated elements.
     * @private
     */
    async _insertElements(elementsToInsert) {
        const insertedElements = [];
        for (let i = 0; i < elementsToInsert.length; i++) {
            const sourceElement = elementsToInsert[i];
            const sourceSelector = Object.keys(this.mappings)[i];
            const target = this.mappings[sourceSelector];
            if (sourceElement) {
                const updatedElements = this._updateDOMElements(sourceElement, target);
                insertedElements.push(...updatedElements);
            }
        }
        this._preparedData = null;
        return insertedElements;
    }

    /**
     * Updates the DOM based on the source and target selectors, and returns an array of updated elements.
     * @param {Element} sourceElement - The source element in the parsed HTML.
     * @param {string|Element} target - The CSS selector or DOM element that is the target in the current DOM.
     * @returns {Element[]} An array of elements that were updated in the DOM.
     * @private
     */
    _updateDOMElements(sourceElement, target) {
        const updatedElements = [];

        if (!sourceElement) {
            this.log("error", "No source element provided", sourceElement);
            return updatedElements;
        }

        if (typeof target === "string") {
            this.document.querySelectorAll(target).forEach((elem) => {
                const clonedElement = sourceElement.cloneNode(true);
                elem.replaceWith(clonedElement);
                updatedElements.push(clonedElement);
            });
        } else if (target instanceof Element) {
            const clonedElement = sourceElement.cloneNode(true);
            target.replaceWith(clonedElement);
            updatedElements.push(clonedElement);
        } else {
            this.log(
                "error",
                "Invalid target type. Target must be a CSS selector string or a DOM node.",
            );
        }

        executeScriptsInNode(sourceElement, this.document);

        return updatedElements;
    }

    log(level, message, objs) {
        console[level || "log"](`${LOG_TAG} ${message}`, objs);
    }
}
